import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { formatPhone, validatePhone, COUNTRY_CONFIGS } from '../utils/formatters';

const PAGE_SIZE = 10;
const API = '/api';

const Icon = ({ d, size = 16, color = 'currentColor', strokeWidth = 2, style = {} }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" style={style}>
        {Array.isArray(d) ? d.map((path, i) => <path key={i} d={path} />) : <path d={d} />}
    </svg>
);

const icons = {
    plus: 'M12 5v14 M5 12h14',
    search: 'M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z',
    edit: 'M11 4H4a2 2 0 00-2 2v14a2 2 0 00 2 2h14a2 2 0 00 2-2v-7 M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z',
    trash: 'M3 6h18 M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2 M10 11v6 M14 11v6',
    x: 'M18 6L6 18 M6 6l12 12',
    building: 'M3 21h18M3 7v1a3 3 0 006 0V4m0 4a3 3 0 006 0V4m0 4a3 3 0 006 0V7H3',
    user: 'M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2M12 7a4 4 0 100-8 4 4 0 000 8z',
    phone: 'M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z',
    mail: 'M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z M22 6l-10 7L2 6',
    mapPin: 'M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 1118 0z M12 13a3 3 0 100-6 3 3 0 000 6z',
    chevronLeft: 'M15 18l-6-6 6-6',
    chevronRight: 'M9 18l6-6-6-6',
    refresh: 'M23 4v6h-6M1 20v-6h6M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15',
    upload: 'M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4 M17 8l-5-5-5 5 M12 3v12',
    download: 'M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4 M7 10l5 5 5-5 M12 15V3',
    lock: 'M19 11H5a2 2 0 00-2 2v7a2 2 0 002 2h14a2 2 0 002-2v-7a2 2 0 00-2-2z M7 11V7a5 5 0 0110 0v4',
    eye: 'M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z M12 9a3 3 0 100 6 3 3 0 000-6z',
    eyeOff: 'M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94 M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19 M1 1l22 22',
};

const defaultForm = {
    companyName: '',
    address: '',
    contactPerson: '',
    phone: '',
    countryCode: '',
    email: '',
    password: '',
    status: 'Active',
    industry: '',
    notes: '',
};

export default function ManageClients({ user, onNavigate, operatorDetails }) {
    const [clients, setClients] = useState([]);
    const [industries, setIndustries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [showModal, setShowModal] = useState(false);
    const [form, setForm] = useState(defaultForm);
    const [editingId, setEditingId] = useState(null);
    const [toast, setToast] = useState(null);
    const [showPassword, setShowPassword] = useState(false);
    const addressInputRef = useRef(null);
    const fileInputRef = useRef(null);
    const [importing, setImporting] = useState(false);

    const showToast = (msg, type = 'success') => {
        setToast({ msg, type });
        setTimeout(() => setToast(null), 3000);
    };

    const fetchClients = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API}/clients/${user?.operatorId || user?._id || 'all'}`);
            setClients(res.data);
        } catch (err) {
            console.error('Fetch clients error:', err);
        } finally {
            setLoading(false);
        }
    };

    const fetchIndustries = async () => {
        try {
            const res = await axios.get(`${API}/master-data`);
            setIndustries(res.data.filter(i => i.category === 'client-industry'));
        } catch (err) {
            console.error('Fetch industries error:', err);
        }
    };

    const downloadTemplate = () => {
        const templateData = [
            {
                'Company Name': 'Test Client Corp',
                'Address': '123 Business Bay, Dubai',
                'Contact Person': 'Jane Smith',
                'Phone': '501122334',
                'Country Code': '+971',
                'Email': 'jane@testclient.com',
                'Industry': 'Technology',
                'Status': 'Active',
                'Notes': 'High priority client'
            }
        ];
        const ws = XLSX.utils.json_to_sheet(templateData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Clients');
        XLSX.writeFile(wb, 'Clients_Template.xlsx');
    };

    const handleImport = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setImporting(true);
        const reader = new FileReader();
        reader.onload = async (evt) => {
            try {
                const bstr = evt.target.result;
                const wb = XLSX.read(bstr, { type: 'binary' });
                const ws = wb.Sheets[wb.SheetNames[0]];
                const data = XLSX.utils.sheet_to_json(ws);

                const mapped = data.map(row => ({
                    companyName: row['Company Name'],
                    address: row['Address'],
                    contactPerson: row['Contact Person'],
                    phone: row['Phone'],
                    countryCode: row['Country Code'] || '+971',
                    email: row['Email'],
                    industry: row['Industry'],
                    status: row['Status'] || 'Active',
                    notes: row['Notes']
                }));

                const res = await fetch('/api/clients/import', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ operatorId: user?.operatorId || user?._id, clients: mapped })
                });
                const result = await res.json();
                if (res.ok) {
                    showToast(`${result.successCount} clients imported!`);
                    fetchClients();
                } else {
                    showToast(result.message || 'Import failed', 'error');
                }
            } catch (err) {
                showToast('Format error', 'error');
            } finally {
                setImporting(false);
                e.target.value = '';
            }
        };
        reader.readAsBinaryString(file);
    };

    useEffect(() => {
        fetchClients();
        fetchIndustries();
    }, [user]);

    useEffect(() => {
        if (showModal && window.google && addressInputRef.current) {
            const autocomplete = new window.google.maps.places.Autocomplete(addressInputRef.current);
            autocomplete.addListener('place_changed', () => {
                const place = autocomplete.getPlace();
                if (place.formatted_address) {
                    setForm(prev => ({ ...prev, address: place.formatted_address }));
                }
            });
        }
    }, [showModal]);

    const filtered = useMemo(() => {
        const q = search.toLowerCase();
        return clients.filter(c => {
            return !q ||
                (c.companyName || '').toLowerCase().includes(q) ||
                (c.contactPerson || '').toLowerCase().includes(q) ||
                (c.email || '').toLowerCase().includes(q) ||
                (c.phone || '').toLowerCase().includes(q) ||
                (c.address || '').toLowerCase().includes(q);
        });
    }, [clients, search]);

    const stats = useMemo(() => {
        return {
            total: filtered.length,
            active: filtered.filter(c => c.status === 'Active').length,
            inactive: filtered.filter(c => c.status === 'Inactive').length,
        };
    }, [filtered]);

    const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    const pageData = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

    const [saving, setSaving] = useState(false);

    const handleSave = async (e) => {
        e.preventDefault();

        // Validate phone number based on operator country
        if (form.phone && !validatePhone(form.phone, operatorDetails?.country)) {
            showToast(`Phone number is invalid for ${operatorDetails?.country || 'the selected country'}.`, 'error');
            return;
        }

        setSaving(true);
        try {
            const opId = user?.operatorId || user?._id;
            if (!opId) throw new Error('Operator ID not found. Please log in again.');

            const { _id, __v, createdAt, updatedAt, ...sanitized } = form;
            const payload = { ...sanitized, operatorId: opId };

            // For updates, only send password if it's non-empty to avoid overwriting with empty string
            // For new clients, always send whatever is in the form (could be empty string if not required)
            if (editingId && !payload.password) {
                delete payload.password;
            }

            if (editingId) {
                await axios.put(`${API}/clients/${editingId}`, payload);
                showToast('Client updated successfully');
            } else {
                await axios.post(`${API}/clients`, payload);
                showToast('Client added successfully');
            }
            await fetchClients();
            closeModal();
        } catch (err) {
            console.error('Save client error:', err);
            const msg = err.response?.data?.message || err.message || 'Error saving client';
            showToast(msg, 'error');
        } finally {
            setSaving(false);
        }
    };


    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this client?')) return;
        try {
            await axios.delete(`${API}/clients/${id}`);
            showToast('Client deleted successfully');
            fetchClients();
        } catch (err) {
            console.error('Delete client error:', err);
            showToast('Error deleting client', 'error');
        }
    };

    const openModal = (client = null) => {
        if (client) {
            setForm({ ...client, password: client.password || '' });
            setEditingId(client._id);
        } else {
            const config = COUNTRY_CONFIGS[operatorDetails?.country] || Object.values(COUNTRY_CONFIGS)[0];
            setForm({ ...defaultForm, countryCode: config?.phoneCode || '+91' });
            setEditingId(null);
        }
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setForm(defaultForm);
        setEditingId(null);
    };

    return (
        <div className="av-page">
            <style>{`
                .av-page { padding: 30px; background: #f8fafc; min-height: 100vh; font-family: 'Inter', sans-serif; }

                /* Header */
                .av-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 24px; }
                .av-title { font-size: 28px; font-weight: 800; color: #1e293b; margin: 0 0 4px 0; letter-spacing: -0.02em; }
                .av-subtitle { font-size: 14px; color: #64748b; margin: 0; font-weight: 500; }
                .av-add-btn {
                    background: #1e1b4b; color: white; border: none; padding: 12px 24px;
                    border-radius: 12px; font-weight: 700; cursor: pointer; display: flex; align-items: center; gap: 10px;
                    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1); box-shadow: 0 4px 12px rgba(30, 27, 75, 0.25); font-size: 14px;
                }
                .av-add-btn:hover { background: #2d2a6b; transform: translateY(-2px); box-shadow: 0 8px 20px rgba(30, 27, 75, 0.3); }

                /* Premium Buttons */
                .premium-btn {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    padding: 12px 24px;
                    border-radius: 14px;
                    font-weight: 700;
                    font-size: 14px;
                    cursor: pointer;
                    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                    border: none;
                    color: white;
                    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
                    white-space: nowrap;
                }
                .premium-btn:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
                }
                .premium-btn:active {
                    transform: translateY(0);
                }
                .btn-import {
                    background: linear-gradient(135deg, #10b981 0%, #059669 100%);
                }
                .btn-template {
                    background: linear-gradient(135deg, #6366f1 0%, #4f46e5 100%);
                }
                .btn-primary {
                    background: #1e1b4b;
                    box-shadow: 0 4px 12px rgba(30, 27, 75, 0.25);
                }
                .premium-btn:disabled {
                    opacity: 0.6;
                    cursor: not-allowed;
                    transform: none;
                }

                /* Stats grid */
                .av-stats-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; margin-bottom: 24px; }
                .av-stat-card {
                    background: white; padding: 22px; border-radius: 20px; border: 1px solid #e2e8f0;
                    display: flex; align-items: center; gap: 18px; transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                    box-shadow: 0 1px 3px rgba(0,0,0,0.02);
                }
                .av-stat-card:hover { transform: translateY(-3px); box-shadow: 0 12px 24px -8px rgba(0,0,0,0.08); border-color: #cbd5e1; }
                .av-stat-icon {
                    width: 52px; height: 52px; border-radius: 14px; display: flex; align-items: center; justify-content: center;
                    font-size: 24px; flex-shrink: 0; transition: all 0.3s;
                }
                .av-stat-card:hover .av-stat-icon { transform: scale(1.1); }
                .av-stat-label { font-size: 13px; font-weight: 600; color: #64748b; margin-bottom: 2px; }
                .av-stat-value { font-size: 24px; font-weight: 800; color: #1e293b; line-height: 1; }

                /* Content Box */
                .av-content {
                    background: white; border-radius: 20px; border: 1px solid #e2e8f0;
                    box-shadow: 0 4px 20px rgba(0,0,0,0.03); overflow: hidden;
                }

                /* Toolbar */
                .av-toolbar {
                    display: flex; justify-content: space-between; align-items: center;
                    padding: 20px 24px; border-bottom: 1px solid #f1f5f9; background: #fff;
                }
                .av-toolbar-left { display: flex; align-items: center; gap: 12px; }
                .av-toolbar-left h3 { font-size: 16px; font-weight: 700; color: #1e293b; margin: 0; }
                .av-refresh-btn {
                    border: 1.5px solid #e2e8f0; background: white; padding: 8px; border-radius: 10px;
                    cursor: pointer; color: #64748b; transition: all 0.2s; display: flex; align-items: center;
                }
                .av-refresh-btn:hover { border-color: #1e1b4b; color: #1e1b4b; transform: rotate(180deg); }
                
                .av-search-wrap { position: relative; display: flex; align-items: center; gap: 10px; }
                .av-search-wrap svg { position: absolute; left: 14px; color: #94a3b8; }
                .av-search {
                    padding: 10px 16px 10px 42px; border: 2.5px solid #f1f5f9; border-radius: 12px;
                    font-size: 14px; color: #1e293b; background: #f8fafc; outline: none;
                    width: 320px; transition: all 0.2s; font-family: 'Inter', sans-serif;
                    font-weight: 500;
                }
                .av-search:focus { border-color: #1e1b4b; background: white; box-shadow: 0 0 0 4px rgba(30, 27, 75, 0.05); }

                /* Table */
                .av-table-wrap { overflow-x: auto; }
                .av-table { width: 100%; border-collapse: collapse; }
                .av-table th {
                    padding: 16px 24px; text-align: left; font-size: 11px; font-weight: 800;
                    color: #64748b; text-transform: uppercase; letter-spacing: 0.12em;
                    border-bottom: 1px solid #e2e8f0; background: #fafbfc;
                }
                .av-table td { padding: 18px 24px; border-bottom: 1px solid #f1f5f9; vertical-align: middle; }
                .av-table tr:hover { background: #fbfcfe; }
                .av-table tr:last-child td { border-bottom: none; }

                .av-client-info { display: flex; align-items: center; gap: 14px; }
                .av-client-avatar {
                    width: 44px; height: 44px; background: linear-gradient(135deg, #1e1b4b 0%, #312e81 100%);
                    color: white; display: flex; align-items: center; justify-content: center;
                    border-radius: 12px; font-weight: 800; font-size: 18px; flex-shrink: 0;
                    box-shadow: 0 4px 10px rgba(30, 27, 75, 0.2);
                }
                .av-company-name { 
                    font-weight: 700; color: #1e293b; font-size: 15px; display: block; margin-bottom: 2px; 
                    text-decoration: none; transition: all 0.2s;
                }
                a.av-company-name:hover { color: #1e1b4b; text-decoration: underline; cursor: pointer; }
                .av-company-address { font-size: 12px; color: #64748b; font-weight: 500; }

                .av-contact-name { font-weight: 700; color: #334155; font-size: 14px; }
                .av-contact-detail { font-size: 13px; color: #64748b; margin-top: 4px; display: flex; align-items: center; gap: 6px; font-weight: 500; }

                /* Status */
                .av-status-badge {
                    padding: 6px 14px; border-radius: 99px; font-size: 11px; font-weight: 800;
                    display: inline-flex; align-items: center; gap: 6px; text-transform: uppercase;
                    letter-spacing: 0.05em;
                }
                .av-status-badge.active { background: #f0fdf4; color: #166534; border: 1.5px solid #dcfce7; }
                .av-status-badge.inactive { background: #fef2f2; color: #991b1b; border: 1.5px solid #fee2e2; }
                .av-status-dot { width: 7px; height: 7px; border-radius: 50%; background: currentColor; }

                /* Actions */
                .av-actions { display: flex; gap: 8px; }
                .av-act-btn {
                    border: 1.5px solid #e2e8f0; background: white; padding: 8px; cursor: pointer; border-radius: 10px;
                    color: #94a3b8; transition: all 0.2s; display: flex; align-items: center; justify-content: center;
                }
                .av-act-btn:hover { border-color: #1e1b4b; color: #1e1b4b; background: #fafbfc; transform: scale(1.05); }
                .av-act-btn.delete:hover { border-color: #ef4444; color: #ef4444; background: #fff5f5; }

                /* Pagination */
                .av-pagination {
                    display: flex; justify-content: space-between; align-items: center;
                    padding: 18px 24px; border-top: 1px solid #f1f5f9; background: #fff;
                }
                .av-page-info { font-size: 13px; color: #64748b; font-weight: 500; }
                .av-page-btns { display: flex; gap: 6px; }
                .av-page-btn {
                    width: 38px; height: 38px; border: 1.5px solid #e2e8f0; background: white;
                    border-radius: 10px; cursor: pointer; font-size: 13px; color: #475569;
                    transition: all 0.15s; display: flex; align-items: center; justify-content: center; font-weight: 700;
                }
                .av-page-btn:hover:not(:disabled) { border-color: #1e1b4b; color: #1e1b4b; background: #fafbfc; }
                .av-page-btn.active { background: #1e1b4b; color: white; border-color: #1e1b4b; box-shadow: 0 4px 10px rgba(30, 27, 75, 0.2); }
                .av-page-btn:disabled { opacity: 0.4; cursor: not-allowed; }

                /* Modal */
                .av-modal-overlay {
                    position: fixed; inset: 0; background: rgba(15, 23, 42, 0.45);
                    backdrop-filter: blur(12px); display: flex; align-items: center; justify-content: center; z-index: 2000;
                    animation: fadeIn 0.3s ease;
                }
                @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }

                .av-modal {
                    background: #ffffff; border-radius: 32px; width: 100%; max-width: 720px;
                    box-shadow: 0 30px 60px -12px rgba(15, 23, 42, 0.25); max-height: 94vh; overflow-y: auto;
                    animation: modalPop 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
                    position: relative;
                }
                @keyframes modalPop { from { transform: scale(0.9) translateY(20px); opacity: 0; } to { transform: scale(1) translateY(0); opacity: 1; } }

                .av-modal-header {
                    padding: 32px 40px; border-bottom: 1px solid #f1f5f9;
                    display: flex; justify-content: space-between; align-items: center;
                    background: rgba(255, 255, 255, 0.8); backdrop-filter: blur(10px);
                    position: sticky; top: 0; z-index: 10;
                }
                .av-modal-title { font-size: 26px; font-weight: 800; color: #0f172a; margin: 0; letter-spacing: -0.025em; }
                .av-modal-subtitle { color: #64748b; font-size: 14px; margin-top: 4px; font-weight: 500; }
                .av-modal-close {
                    border: none; background: #f1f5f9; cursor: pointer; color: #64748b; padding: 10px;
                    border-radius: 12px; transition: all 0.3s;
                }
                .av-modal-close:hover { transform: rotate(90deg); background: #e2e8f0; color: #0f172a; }

                .av-modal-body { padding: 40px; }
                .av-form-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 28px; }
                .av-form-group { display: flex; flex-direction: column; gap: 10px; }
                .av-form-group.full { grid-column: span 2; }
                .av-form-group label { font-size: 13px; font-weight: 700; color: #475569; margin-left: 4px; }

                .av-input-wrap { position: relative; display: flex; align-items: center; }
                .av-input-icon { position: absolute; left: 16px; color: #94a3b8; pointer-events: none; transition: all 0.2s; }
                .av-input-wrap input {
                    width: 100%; padding: 13px 16px 13px 48px; border: 2.5px solid #f1f5f9;
                    border-radius: 16px; font-size: 14px; outline: none; transition: all 0.2s;
                    background: #f8fafc; font-weight: 600; color: #1e293b; font-family: 'Inter', sans-serif; box-sizing: border-box;
                }
                .av-input-wrap input:focus { border-color: #1e1b4b; background: white; box-shadow: 0 0 0 4px rgba(30, 27, 75, 0.05); }
                .av-input-wrap input:focus + .av-input-icon { color: #1e1b4b; transform: scale(1.1); }

                .av-select {
                    width: 100%; padding: 13px 16px; border: 2.5px solid #f1f5f9;
                    border-radius: 16px; font-size: 14px; outline: none; transition: all 0.2s;
                    background: #f8fafc; font-weight: 600; color: #1e293b; cursor: pointer;
                    appearance: none; background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%2364748b' stroke-width='2.5'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E");
                    background-repeat: no-repeat; background-position: right 18px center; background-size: 18px;
                }
                .av-textarea {
                    width: 100%; padding: 13px 18px; border: 2.5px solid #f1f5f9;
                    border-radius: 16px; font-size: 14px; outline: none; transition: all 0.2s;
                    background: #f8fafc; font-weight: 500; color: #1e293b; resize: vertical; min-height: 110px; font-family: 'Inter', sans-serif;
                }
                .av-textarea:focus { border-color: #1e1b4b; background: white; box-shadow: 0 0 0 4px rgba(30, 27, 75, 0.05); }

                .av-eye-btn {
                    position: absolute; right: 14px; top: 50%; transform: translateY(-50%);
                    background: none; border: none; cursor: pointer; color: #94a3b8;
                    display: flex; align-items: center; padding: 4px; border-radius: 6px;
                    transition: color 0.2s;
                }
                .av-eye-btn:hover { color: #1e1b4b; }

                .av-modal-footer {
                    padding: 32px 40px; border-top: 1px solid #f1f5f9;
                    display: flex; justify-content: flex-end; gap: 14px;
                    background: rgba(255, 255, 255, 0.8); backdrop-filter: blur(10px);
                    position: sticky; bottom: 0; z-index: 10;
                }
                .av-btn-cancel {
                    padding: 13px 26px; border: 2.5px solid #f1f5f9; background: white;
                    border-radius: 16px; font-size: 14px; font-weight: 700; color: #64748b;
                    cursor: pointer; transition: all 0.2s;
                }
                .av-btn-cancel:hover { border-color: #cbd5e1; color: #1e293b; background: #f8fafc; }
                
                .av-btn-save {
                    padding: 13px 36px; border: none; background: #1e1b4b; color: white;
                    border-radius: 16px; font-size: 14px; font-weight: 800; cursor: pointer;
                    transition: all 0.3s; box-shadow: 0 4px 12px rgba(30, 27, 75, 0.2);
                }
                .av-btn-save:hover { background: #2d2a6b; transform: translateY(-2px); box-shadow: 0 8px 18px rgba(30, 27, 75, 0.3); }

                /* Toast */
                .av-toast {
                    position: fixed; top: 24px; right: 24px; padding: 18px 28px; border-radius: 16px;
                    color: white; z-index: 3000; box-shadow: 0 20px 25px -5px rgba(0,0,0,0.15);
                    font-weight: 800; animation: toastSlide 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
                }
                @keyframes toastSlide { from { transform: translateX(120%); opacity: 0; } to { transform: translateX(0); opacity: 1; } }
                .av-toast.success { background: #10b981; }
                .av-toast.error { background: #ef4444; }

                .av-empty { text-align: center; padding: 100px 24px; color: #94a3b8; font-size: 15px; font-weight: 600; }

                .av-form-section {
                    grid-column: span 2;
                    margin-top: 12px;
                    margin-bottom: 8px;
                    display: flex;
                    align-items: center;
                    gap: 12px;
                }
                .av-form-section:first-child { margin-top: 0; }
                .av-form-section h3 {
                    font-size: 14px;
                    font-weight: 800;
                    color: #1e1b4b;
                    text-transform: uppercase;
                    letter-spacing: 0.1em;
                    margin: 0;
                    white-space: nowrap;
                }
                .av-form-section-line {
                    height: 2px;
                    background: #f1f5f9;
                    flex-grow: 1;
                }
            `}</style>

            {toast && <div className={`av-toast ${toast.type}`}>{toast.msg}</div>}

            <div className="av-header">
                <div>
                    <h1 className="av-title">Manage Clients</h1>
                    <p className="av-subtitle">Track and manage your corporate clients and partners</p>
                </div>
                <div style={{ display: 'flex', gap: 12 }}>
                    <input type="file" ref={fileInputRef} hidden accept=".xlsx,.xls" onChange={handleImport} />
                    <button className="premium-btn btn-import" onClick={() => fileInputRef.current.click()} disabled={importing}>
                        <Icon d={icons.upload} size={18} strokeWidth={2.5} />
                        {importing ? 'Importing...' : 'Import'}
                    </button>
                    <button className="premium-btn btn-template" onClick={downloadTemplate}>
                        <Icon d={icons.download} size={18} strokeWidth={2.5} />
                        Template
                    </button>
                    <button className="premium-btn btn-primary" onClick={() => openModal()}>
                        <Icon d={icons.plus} size={18} strokeWidth={2.5} />
                        Add New Client
                    </button>
                </div>
            </div>

            <div className="av-stats-grid">
                <div className="av-stat-card">
                    <div className="av-stat-icon" style={{ background: '#eff6ff', color: '#1e1b4b' }}>
                        <Icon d={icons.building} size={28} />
                    </div>
                    <div>
                        <div className="av-stat-label">Total Clients</div>
                        <div className="av-stat-value">{stats.total}</div>
                    </div>
                </div>
                <div className="av-stat-card">
                    <div className="av-stat-icon" style={{ background: '#f0fdf4', color: '#16a34a' }}>
                        <div style={{ width: 14, height: 14, borderRadius: '50%', background: 'currentColor' }} />
                    </div>
                    <div>
                        <div className="av-stat-label">Active Companies</div>
                        <div className="av-stat-value">{stats.active}</div>
                    </div>
                </div>
                <div className="av-stat-card">
                    <div className="av-stat-icon" style={{ background: '#fef2f2', color: '#dc2626' }}>
                        <div style={{ width: 14, height: 14, borderRadius: '50%', background: 'currentColor', opacity: 0.5 }} />
                    </div>
                    <div>
                        <div className="av-stat-label">Inactive</div>
                        <div className="av-stat-value">{stats.inactive}</div>
                    </div>
                </div>
            </div>

            <div className="av-content">
                <div className="av-toolbar">
                    <div className="av-toolbar-left">
                        <button className="av-refresh-btn" onClick={fetchClients} title="Refresh Data">
                            <Icon d={icons.refresh} size={18} strokeWidth={2.5} />
                        </button>
                        <h3>All Companies</h3>
                    </div>
                    <div className="av-search-wrap">
                        <Icon d={icons.search} size={18} />
                        <input
                            type="text"
                            placeholder="Search by name, email, or place..."
                            className="av-search"
                            value={search}
                            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                        />
                    </div>
                </div>

                <div className="av-table-wrap">
                    <table className="av-table">
                        <thead>
                            <tr>
                                <th>Company Details</th>
                                <th>Contact Person</th>
                                <th>Contact Info</th>
                                <th>People</th>
                                <th>Status</th>
                                <th style={{ textAlign: 'right' }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan="5" className="av-empty">Loading clients...</td></tr>
                            ) : pageData.length === 0 ? (
                                <tr><td colSpan="5" className="av-empty">No clients found matching your search.</td></tr>
                            ) : (
                                pageData.map(client => (
                                    <tr key={client._id}>
                                        <td>
                                            <div className="av-client-info">
                                                <div className="av-client-avatar">
                                                    {(client.companyName || '?').charAt(0)}
                                                </div>
                                                <div>
                                                    <a href="#" className="av-company-name" onClick={(e) => { e.preventDefault(); onNavigate('client-details', client._id); }}>{client.companyName}</a>
                                                    <span className="av-company-address">{client.address || 'Location not set'}</span>
                                                </div>
                                            </div>
                                        </td>
                                        <td>
                                            <div className="av-contact-name">{client.contactPerson || 'N/A'}</div>
                                        </td>
                                        <td>
                                            <div className="av-contact-detail">
                                                <Icon d={icons.mail} size={13} strokeWidth={2.5} />
                                                {client.email || 'No email'}
                                            </div>
                                            <div className="av-contact-detail">
                                                <Icon d={icons.phone} size={13} strokeWidth={2.5} />
                                                {formatPhone(client.phone, operatorDetails?.country) || 'No phone'}
                                            </div>
                                        </td>
                                        <td>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                                <Icon d={icons.user} size={14} color="#94a3b8" />
                                                <span style={{ fontWeight: 700, color: '#1e1b4b' }}>{(client.contacts || []).length}</span>
                                                <span style={{ fontSize: 11, fontWeight: 600, color: '#64748b' }}>Person(s)</span>
                                            </div>
                                        </td>
                                        <td>
                                            <span className={`av-status-badge ${client.status.toLowerCase()}`}>
                                                <span className="av-status-dot"></span>
                                                {client.status}
                                            </span>
                                        </td>
                                        <td>
                                            <div className="av-actions" style={{ justifyContent: 'flex-end' }}>
                                                <button className="av-act-btn" title="Edit" onClick={() => openModal(client)}>
                                                    <Icon d={icons.edit} size={16} strokeWidth={2.5} />
                                                </button>
                                                <button className="av-act-btn delete" title="Delete" onClick={() => handleDelete(client._id)}>
                                                    <Icon d={icons.trash} size={16} strokeWidth={2.5} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {filtered.length > PAGE_SIZE && (
                    <div className="av-pagination">
                        <span className="av-page-info">
                            Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filtered.length)} of {filtered.length}
                        </span>
                        <div className="av-page-btns">
                            <button className="av-page-btn" disabled={page === 1} onClick={() => setPage(p => p - 1)}>
                                <Icon d={icons.chevronLeft} size={18} strokeWidth={2.5} />
                            </button>
                            {[...Array(totalPages)].map((_, i) => (
                                <button
                                    key={i}
                                    className={`av-page-btn ${page === i + 1 ? 'active' : ''}`}
                                    onClick={() => setPage(i + 1)}
                                >
                                    {i + 1}
                                </button>
                            ))}
                            <button className="av-page-btn" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>
                                <Icon d={icons.chevronRight} size={18} strokeWidth={2.5} />
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {showModal && (
                <div className="av-modal-overlay">
                    <form className="av-modal" onSubmit={handleSave}>
                        <div className="av-modal-header">
                            <div>
                                <h2 className="av-modal-title">{editingId ? 'Update Client' : 'Add New Client'}</h2>
                                <p className="av-modal-subtitle">Enter company and contact information</p>
                            </div>
                            <button type="button" className="av-modal-close" onClick={closeModal}>
                                <Icon d={icons.x} size={20} strokeWidth={2.5} />
                            </button>
                        </div>

                        <div className="av-modal-body">
                            <div className="av-form-grid">
                                {/* Section: Company Information */}
                                <div className="av-form-section">
                                    <h3>Company Information</h3>
                                    <div className="av-form-section-line"></div>
                                </div>

                                <div className="av-form-group full">
                                    <label>Company Name</label>
                                    <div className="av-input-wrap">
                                        <Icon d={icons.building} size={18} className="av-input-icon" strokeWidth={2.5} />
                                        <input
                                            type="text"
                                            required
                                            value={form.companyName}
                                            onChange={(e) => setForm({ ...form, companyName: e.target.value })}
                                            placeholder="e.g. Infosys Ltd"
                                        />
                                    </div>
                                </div>

                                <div className="av-form-group full">
                                    <label>Address / Place</label>
                                    <div className="av-input-wrap">
                                        <Icon d={icons.mapPin} size={18} className="av-input-icon" strokeWidth={2.5} />
                                        <input
                                            ref={addressInputRef}
                                            type="text"
                                            value={form.address}
                                            onChange={(e) => setForm({ ...form, address: e.target.value })}
                                            placeholder="Company location (Autocomplete)"
                                        />
                                    </div>
                                </div>

                                <div className="av-form-group">
                                    <label>Industry</label>
                                    <select
                                        className="av-select"
                                        value={form.industry}
                                        onChange={(e) => setForm({ ...form, industry: e.target.value })}
                                    >
                                        <option value="">Select Industry</option>
                                        {industries.map(ind => (
                                            <option key={ind._id} value={ind.name}>{ind.name}</option>
                                        ))}
                                    </select>
                                </div>

                                <div className="av-form-group">
                                    <label>Status</label>
                                    <select
                                        className="av-select"
                                        value={form.status}
                                        onChange={(e) => setForm({ ...form, status: e.target.value })}
                                    >
                                        <option value="Active">Active</option>
                                        <option value="Inactive">Inactive</option>
                                    </select>
                                </div>

                                {/* Section: Contact Information */}
                                <div className="av-form-section">
                                    <h3>Contact Information</h3>
                                    <div className="av-form-section-line"></div>
                                </div>

                                <div className="av-form-group">
                                    <label>Contact Person</label>
                                    <div className="av-input-wrap">
                                        <Icon d={icons.user} size={18} className="av-input-icon" strokeWidth={2.5} />
                                        <input
                                            type="text"
                                            value={form.contactPerson}
                                            onChange={(e) => setForm({ ...form, contactPerson: e.target.value })}
                                            placeholder="Full name"
                                        />
                                    </div>
                                </div>

                                <div className="av-form-group">
                                    <label>Email Address</label>
                                    <div className="av-input-wrap">
                                        <Icon d={icons.mail} size={18} className="av-input-icon" strokeWidth={2.5} />
                                        <input
                                            type="email"
                                            value={form.email}
                                            onChange={(e) => setForm({ ...form, email: e.target.value })}
                                            placeholder="contact@company.com"
                                        />
                                    </div>
                                </div>

                                <div className="av-form-group full">
                                    <label>Phone Number</label>
                                    <div className="av-input-wrap" style={{ gap: '8px' }}>
                                        <select
                                            className="av-select"
                                            style={{ width: '100px', flexShrink: 0 }}
                                            value={form.countryCode}
                                            onChange={(e) => setForm({ ...form, countryCode: e.target.value })}
                                        >
                                            {Object.values(COUNTRY_CONFIGS).map(c => (
                                                <option key={c.phoneCode} value={c.phoneCode}>{c.phoneCode}</option>
                                            ))}
                                        </select>
                                        <input
                                            type="text"
                                            style={{ paddingLeft: '16px' }}
                                            value={form.phone}
                                            onChange={(e) => setForm({ ...form, phone: e.target.value })}
                                            placeholder="Enter phone number"
                                        />
                                    </div>
                                </div>

                                {/* Section: Password & Security */}
                                <div className="av-form-section">
                                    <h3>Login Credentials</h3>
                                    <div className="av-form-section-line"></div>
                                </div>

                                <div className="av-form-group">
                                    <label>Access Password</label>
                                    <div className="av-input-wrap">
                                        <Icon d={icons.lock} size={18} className="av-input-icon" strokeWidth={2.5} />
                                        <input
                                            type={showPassword ? 'text' : 'password'}
                                            value={form.password || ''}
                                            onChange={(e) => setForm({ ...form, password: e.target.value })}
                                            placeholder={editingId ? 'Leave blank to keep unchanged' : 'Set client password'}
                                            style={{ paddingRight: '44px' }}
                                        />
                                        <button
                                            type="button"
                                            className="av-eye-btn"
                                            onClick={() => setShowPassword(v => !v)}
                                            tabIndex={-1}
                                        >
                                            <Icon d={showPassword ? icons.eyeOff : icons.eye} size={16} strokeWidth={2} />
                                        </button>
                                    </div>
                                </div>

                                {/* Section: Additional Info */}
                                <div className="av-form-section">
                                    <h3>Other Details</h3>
                                    <div className="av-form-section-line"></div>
                                </div>

                                <div className="av-form-group full">
                                    <label>Additional Notes</label>
                                    <textarea
                                        className="av-textarea"
                                        value={form.notes}
                                        onChange={(e) => setForm({ ...form, notes: e.target.value })}
                                        placeholder="Any other details..."
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="av-modal-footer">
                            <button type="button" className="av-btn-cancel" onClick={closeModal} disabled={saving}>Cancel</button>
                            <button type="submit" className="av-btn-save" disabled={saving}>
                                {saving ? (editingId ? 'Updating...' : 'Saving...') : (editingId ? 'Update Client' : 'Save Client')}
                            </button>
                        </div>

                    </form>
                </div>
            )}
        </div>
    );
}
